import { DragAndDropUploadData } from "@/components/DragAndDropFields";
import { DragAndDropAcceptFileType } from "@/components/DragAndDropFields";
import { Button, Label, Textarea } from "@fluentui/react-components";
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router";

import { postAdminCreateFrontendAddon } from "@/api/admin/frontendAddons";
import { postCreateFrontendAddon } from "@/api/frontendAddons";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useFrontendAddons } from "@/components/InOrganizationProvider/FrontendAddonsProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useAppRouteParams } from "../../../AppRoutes";
import { DialogPhaseType, useDialog } from "../../Dialog/helpers/hooks";
import { LayoutTemplate } from "../../LayoutTemplate";
import { FrontendAddonFileField } from "../helpers/FrontendAddonFileField";
import { WarningMessageForProduction } from "../helpers/WarningMessageForProduction";
import layoutStyles from "../helpers/formLayout.module.css";
import styles from "./index.module.css";

const initialFrontendAddonRaw = {
  description: "",
};

export const FrontendAddonFormCreate: React.FC<{
  returnPath: string;
}> = ({ returnPath }) => {
  const isProd = process.env.APP_ENV === "production";
  const navigate = useNavigate();
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");

  const { organizationId } = useAppRouteParams();
  const { runReloadFrontendAddons } = useFrontendAddons();
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const { renderDialogs, isInProgress } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  const [newFrontendAddon, setNewFrontendAddon] = useState(
    initialFrontendAddonRaw
  );

  const [uploadedZipFileList, setUploadedZipFileList] = useState<
    DragAndDropUploadData[]
  >([]);

  const runCreateFrontendAddon = useCallback(async () => {
    setDialogPhase("in_progress");
    try {
      isAADeveloperOrAbove
        ? await postAdminCreateFrontendAddon({
            organizationId,
            FrontendAddonRawForm: {
              ...newFrontendAddon,
              privacy_setting: "private",
              owner_org_id: organizationId,
            },
            file: uploadedZipFileList[0].file,
          })
        : await postCreateFrontendAddon({
            organizationId,
            FrontendAddonRawForm: {
              ...newFrontendAddon,
              privacy_setting: "private",
              owner_org_id: organizationId,
            },
            file: uploadedZipFileList[0].file,
          });
      await runReloadFrontendAddons();
      showSuccessNotification("フロントエンドアドオンを作成しました");
      navigate(returnPath);
    } catch (error) {
      showErrorNotification(
        "フロントエンドアドオンの作成に失敗しました",
        error
      );
    }
    setDialogPhase(null);
  }, [
    newFrontendAddon,
    uploadedZipFileList,
    organizationId,
    isAADeveloperOrAbove,
    navigate,
    returnPath,
    runReloadFrontendAddons,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const isDisableForm = !hasWritePermission || isInProgress;
  const renderFormBody = () => (
    <div className={layoutStyles.formField}>
      <div className={layoutStyles.inputContainer}>
        <Label>概要</Label>
        <Textarea
          className={styles.descriptionTextarea}
          resize="vertical"
          value={newFrontendAddon.description}
          onChange={(_, data) => {
            setNewFrontendAddon((state) => ({
              ...state,
              description: data.value,
            }));
          }}
          disabled={isDisableForm}
        />
      </div>
      <FrontendAddonFileField
        uploadedFileList={uploadedZipFileList}
        setUploadedFileList={setUploadedZipFileList}
        existingType={existingType}
        setExistingType={setExistingType}
        isDisableForm={isDisableForm}
      />
    </div>
  );

  const isDisableSave = isDisableForm || uploadedZipFileList?.length !== 1;

  const renderRightHeader = () => {
    return (
      <div className={layoutStyles.buttonContainer}>
        <Button
          appearance="primary"
          disabled={isDisableSave}
          onClick={() => setDialogPhase("confirm")}
        >
          作成
        </Button>
        <Button
          appearance="secondary"
          onClick={() => {
            navigate(returnPath);
          }}
          disabled={isInProgress}
        >
          キャンセル
        </Button>
      </div>
    );
  };

  return (
    <>
      <div className={layoutStyles.cardContainer}>
        <LayoutTemplate
          bodyElement={renderFormBody()}
          leftHeaderElement={<>フロントエンドアドオン作成</>}
          rightHeaderElement={renderRightHeader()}
        />
      </div>

      {renderDialogs({
        confirm: {
          element: isProd ? (
            <WarningMessageForProduction isEdit={false} />
          ) : (
            <>フロントエンドアドオンを作成しますか？</>
          ),
          title: "フロントエンドアドオン作成",
          buttonText: "作成",
          onClick: runCreateFrontendAddon,
        },
        inProgress: {
          title: "フロントエンドアドオンの作成中",
        },
      })}
    </>
  );
};
